/* ─────────────────────────────────────────────────────────────────────
   Tykables 7.0 — Optimistic cart UI (tyk-optimistic-cart.js)

   Closes the perceived-latency gap between "user clicks Add" and "cart
   confirms" by updating the visible cart count immediately, then letting
   the real /cart/add.js round-trip reconcile silently.

   Scope (deliberately conservative):
     1. Cart count badge bumps optimistically on Add-to-Cart button
        clicks. The existing PubSub flow updates the badge again with the
        server-confirmed count once /cart/add.js completes — if Shopify
        rejected the add (out of stock, etc.), the badge snaps back.
     2. Cart drawer quantity steppers (+/-) show an immediate visual
        "pending" state on the row until the server response arrives.
        We don't change the rendered total ourselves — Dawn does that
        from the response — but the row pulses subtly so it doesn't
        feel frozen.

   Out of scope (intentional):
     • Optimistic line-total / subtotal recompute. That requires
       client-side knowledge of every Shopify discount + tax + shipping
       rule, which is unreliable. Defer to server.
     • Optimistic cart drawer line-item insertion. Same reason.

   Failure handling:
     • Listen for cartError PubSub events; show a toast and rely on the
       cart-update event that follows to correct the badge.
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const SELECTORS = {
    addToCart: 'button[type="submit"][name="add"], [data-tyk-add-btn], .product-form__submit, [data-add-to-cart]',
    qtyMinus: '[data-tyk-cart-minus], cart-drawer button[name="minus"], .quantity__button[name="minus"]',
    qtyPlus:  '[data-tyk-cart-plus], cart-drawer button[name="plus"], .quantity__button[name="plus"]',
    badge:    '#cart-icon-bubble [data-cart-count], .cart-count-bubble [data-cart-count], .tyk-mobnav__badge, #cart-icon-bubble .visually-hidden + span, .cart-count, [data-tyk-cart-count]',
    qtyInputs: 'input[name^="quantity"], input[name^="updates["]',
    cartLineRow: 'cart-drawer .cart-item, .tyk-cart-row, tr.cart-item',
  };

  // ── Badge helpers ───────────────────────────────────────────────────

  function findBadgeNumeric() {
    // Try to read the current numeric badge value from any visible element.
    const candidates = document.querySelectorAll(SELECTORS.badge);
    for (let i = 0; i < candidates.length; i++) {
      const text = (candidates[i].textContent || '').trim();
      const n = parseInt(text, 10);
      if (!Number.isNaN(n)) return n;
    }
    return 0;
  }

  function bumpBadge(delta) {
    if (!delta) return;
    const next = Math.max(0, findBadgeNumeric() + delta);
    document.querySelectorAll(SELECTORS.badge).forEach(function (el) {
      // Skip aria-only elements (visually-hidden) — they update via PubSub
      if (el.classList && el.classList.contains('visually-hidden')) return;
      el.textContent = String(next);
      // Reveal hidden badges (mobile nav badge has is-hidden when count===0)
      el.classList.remove('is-hidden', 'hidden');
    });
  }

  // ── Pending-state helper ────────────────────────────────────────────

  function markPending(row, on) {
    if (!row) return;
    if (on) {
      row.setAttribute('data-tyk-pending', '1');
      row.style.opacity = '0.65';
    } else {
      row.removeAttribute('data-tyk-pending');
      row.style.opacity = '';
    }
  }

  function clearAllPending() {
    document.querySelectorAll('[data-tyk-pending]').forEach(function (row) {
      markPending(row, false);
    });
  }

  // ── Add-to-Cart click handler ───────────────────────────────────────

  function readQuantityInScope(form) {
    if (!form) return 1;
    const inp = form.querySelector('input[name="quantity"], [data-tyk-qty-input]');
    if (!inp) return 1;
    const n = parseInt(inp.value, 10);
    return (Number.isFinite(n) && n > 0) ? n : 1;
  }

  function onAddClick(e) {
    const btn = e.target && e.target.closest && e.target.closest(SELECTORS.addToCart);
    if (!btn) return;
    if (btn.disabled) return;
    const form = btn.closest('form');
    const qty = readQuantityInScope(form);
    // Optimistic bump
    bumpBadge(qty);
    // Subtle haptic cue at the same moment
    if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('tap');
  }

  // ── Quantity stepper click handler ──────────────────────────────────

  function onQtyClick(e) {
    const isMinus = e.target.closest && e.target.closest(SELECTORS.qtyMinus);
    const isPlus  = e.target.closest && e.target.closest(SELECTORS.qtyPlus);
    if (!isMinus && !isPlus) return;
    const row = (isMinus || isPlus).closest(SELECTORS.cartLineRow) ||
                (isMinus || isPlus).closest('.cart-item, tr');
    if (row) {
      markPending(row, true);
      // Auto-clear after 4s in case the server response gets lost / no
      // PubSub event fires (graceful degradation)
      setTimeout(() => markPending(row, false), 4000);
    }
  }

  // ── Hook PubSub to clear pending state when server responds ─────────

  function wirePubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      clearAllPending();
    });

    subscribe(PUB_SUB_EVENTS.cartError, function (e) {
      clearAllPending();
      // Snap the badge back by re-reading from server state via /cart.js
      reconcileBadge();
      if (window.tykNative && window.tykNative.toast) {
        const msg = (e && e.message) || 'Could not update cart. Please try again.';
        window.tykNative.toast(msg, 'error');
      }
    });
  }

  function reconcileBadge() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then((r) => r.ok ? r.json() : null)
      .then((cart) => {
        if (!cart || typeof cart.item_count !== 'number') return;
        document.querySelectorAll(SELECTORS.badge).forEach(function (el) {
          if (el.classList && el.classList.contains('visually-hidden')) return;
          el.textContent = String(cart.item_count);
          if (cart.item_count === 0) el.classList.add('is-hidden');
          else el.classList.remove('is-hidden', 'hidden');
        });
      })
      .catch(() => {});
  }

  // ── Init ────────────────────────────────────────────────────────────

  function init() {
    document.addEventListener('click', onAddClick, true); // capture so we run before form-submit handlers
    document.addEventListener('click', onQtyClick, true);
    wirePubSub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
